import {
  type IconName,
  SelectField,
  type SelectFieldOption,
} from '@wandb/components';
import React, {type FC, useMemo} from 'react';

import {
  AccessOption,
  getDescriptionForProjectAccess,
  getDisplayNameForProjectAccess,
  getIconForProjectAccess,
} from '../../../util/permissions';

interface ProjectAccessOptionsProps {
  selectedProjectAccess: AccessOption | undefined;
  setSelectedProjectAccess: (accessOption: AccessOption) => void;
  projectAccessOptions: AccessOption[];
  isTeamProject: boolean;
  isRestrictedProjectEnabled: boolean;
}

export const ProjectAccessOptions: FC<ProjectAccessOptionsProps> = ({
  selectedProjectAccess,
  setSelectedProjectAccess,
  projectAccessOptions,
  isTeamProject,
  isRestrictedProjectEnabled,
}) => {
  const isPremiumFeatureEnabled = isRestrictedProjectEnabled && isTeamProject;

  const items: SelectFieldOption[] = useMemo(
    () =>
      projectAccessOptions.map(o => ({
        id: o,
        label: getDisplayNameForProjectAccess(o, isTeamProject),
        description: getDescriptionForProjectAccess(o, isTeamProject),
        decorationLeft: {
          iconProps: {
            icon: getIconForProjectAccess(o, isTeamProject) as IconName,
          },
        },
        ...(o === AccessOption.Restricted && !isPremiumFeatureEnabled
          ? {
              decorationRight: {
                pillProps: {
                  icon: 'crown-pro' as const,
                  color: 'purple' as const,
                  tooltip: {children: 'Upgrade to enable this feature'},
                },
              },
            }
          : {}),
      })),
    [projectAccessOptions, isTeamProject, isPremiumFeatureEnabled]
  );

  const disabledKeys = useMemo(() => {
    const keys: AccessOption[] = [];
    if (!isPremiumFeatureEnabled) {
      keys.push(AccessOption.Restricted);
    }
    return keys;
  }, [isPremiumFeatureEnabled]);

  return (
    <SelectField
      id="project-access-options"
      label="Project visibility"
      items={items}
      placeholder="Select project visibility"
      value={selectedProjectAccess ?? null}
      onChange={key => {
        if (key != null) {
          setSelectedProjectAccess(key as AccessOption);
        }
      }}
      disabledKeys={disabledKeys}
      fullWidth
      matchTriggerWidth
    />
  );
};
